import getMinMaxDuration from './get-minmax-duration';

/** What a search asks the results page for. */
export interface ResultsCriteria {
  destinationIds: number[];
  hotelIds: number[];
  airportGroupIds: Array<string | number>;
  airportCodes: Array<string | number>;
  /** The duration bucket (e.g. 10707), or null for any. */
  duration: number | string | null;
  /** The departure range searched, or null for any. */
  dates: { min: string; max: string } | null;
  rooms: Array<{ adults: number; childAges: number[] }>;
  /** Search options authored on the page (board and similar). */
  options: Array<string | number>;
}

/**
 * The results page query string for `criteria`.
 *
 * Shared by the search component and the early script that sets the Search
 * link before the component loads, so a click either side of loading goes to
 * the same results.
 */
export const resultsQuery = (criteria: ResultsCriteria): string => {
  const params: Record<string, string> = {};

  // Destinations and accommodations are independent namespaces and search
  // AND-s them, so both are emitted.
  if (criteria.hotelIds.length > 0) {
    params['accommodations'] = criteria.hotelIds.join(',');
  }
  if (criteria.destinationIds.length > 0) {
    params['destinations'] = criteria.destinationIds.join(',');
  }

  if (criteria.airportGroupIds.length > 0) {
    params['airportGroups'] = criteria.airportGroupIds.join(',');
  }
  if (criteria.airportCodes.length > 0) {
    params['airports'] = criteria.airportCodes.join(',');
  }

  const duration =
    criteria.duration === null ? null : getMinMaxDuration(criteria.duration);
  if (duration) {
    params['minDuration'] = duration.min.toString();
    params['maxDuration'] = duration.max.toString();
  }

  if (criteria.dates) {
    params['minDepartureDate'] = criteria.dates.min;
    params['maxDepartureDate'] = criteria.dates.max;
  }

  const adults = criteria.rooms.map(r => r.adults).join(',');
  const children = criteria.rooms.map(r => r.childAges.join(',')).join('|');
  if (adults.length > 0) {
    params['adults'] = adults;
  }
  if (children.length > 0) {
    params['childAges'] = children;
  }

  if (criteria.options.length > 0) {
    params['options'] = criteria.options.join(',');
  }

  return new URLSearchParams(params).toString();
};
